import { useState, useCallback, useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Search, X, MapPin, Loader2, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { getMapStyleUrl, searchAddress, type GeocodingResult } from '@/api/geo';

interface StartPointPickerProps {
  destination: { lat: number; lon: number };
  onSelectPoint: (point: { lat: number; lon: number }) => void;
  onCancel: () => void;
}

export function StartPointPicker({
  destination,
  onSelectPoint,
  onCancel,
}: StartPointPickerProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const startMarker = useRef<maplibregl.Marker | null>(null);
  const destMarker = useRef<maplibregl.Marker | null>(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [startPoint, setStartPoint] = useState<{ lat: number; lon: number } | null>(null);

  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyleUrl(),
      center: [destination.lon, destination.lat],
      zoom: 14,
      attributionControl: false,
    });

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    // Handle map click to place start pin
    map.current.on('click', (e) => {
      setStartPoint({ lat: e.lngLat.lat, lon: e.lngLat.lng });
    });

    return () => {
      map.current?.remove();
      map.current = null;
      setMapLoaded(false);
    };
  }, [destination]);

  // Add destination marker
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (destMarker.current) {
      destMarker.current.remove();
    }

    const el = document.createElement('div');
    el.innerHTML = `
      <div style="
        width: 32px;
        height: 32px;
        background-color: #22c55e;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
          <line x1="4" y1="22" x2="4" y2="15"></line>
        </svg>
      </div>
    `;

    destMarker.current = new maplibregl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([destination.lon, destination.lat])
      .addTo(map.current);
  }, [destination, mapLoaded]);

  // Update start marker when point changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (startMarker.current) {
      startMarker.current.remove();
      startMarker.current = null;
    }

    if (startPoint) {
      const el = document.createElement('div');
      el.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
        ">
          <div style="
            width: 32px;
            height: 32px;
            background-color: #3b82f6;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: grab;
          "></div>
          <div style="
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 12px solid #3b82f6;
            margin-top: -2px;
          "></div>
        </div>
      `;

      startMarker.current = new maplibregl.Marker({
        element: el,
        anchor: 'bottom',
        draggable: true,
      })
        .setLngLat([startPoint.lon, startPoint.lat])
        .addTo(map.current);

      startMarker.current.on('dragend', () => {
        const lngLat = startMarker.current?.getLngLat();
        if (lngLat) {
          setStartPoint({ lat: lngLat.lat, lon: lngLat.lng });
        }
      });
    }
  }, [startPoint, mapLoaded]);

  // Search handlers
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const result = await searchAddress(query, {
        lat: destination.lat,
        lon: destination.lon,
        limit: 5,
      });
      setSearchResults(result.features);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [destination]);

  const handleSelectSearchResult = useCallback((result: GeocodingResult) => {
    const [lon, lat] = result.geometry.coordinates;
    setStartPoint({ lat, lon });
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);

    // Pan to selected point
    map.current?.flyTo({
      center: [lon, lat],
      zoom: 16,
      duration: 500,
    });
  }, []);

  const formatSearchResult = useCallback((result: GeocodingResult): string => {
    const p = result.properties;
    if (p.display_name) {
      const parts = p.display_name.split(', ').slice(0, 3);
      return parts.join(', ');
    }
    return 'Неизвестный адрес';
  }, []);

  const handleConfirm = useCallback(() => {
    if (startPoint) {
      onSelectPoint(startPoint);
    }
  }, [startPoint, onSelectPoint]);

  const handleZoomIn = useCallback(() => {
    map.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    map.current?.zoomOut();
  }, []);

  return (
    <div className="h-screen w-screen relative overflow-hidden bg-background">
      {/* Map */}
      <div
        ref={mapContainer}
        className="w-full h-full"
      />

      {/* Top panel */}
      <div className="absolute top-0 left-0 right-0 z-10 safe-area-inset-top">
        <div className="bg-white/95 backdrop-blur-sm shadow-lg m-3 rounded-xl">
          <div className="p-4">
            <h2 className="font-semibold text-lg mb-2">Выберите начальную точку</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Нажмите на карту или найдите адрес
            </p>

            {/* Search */}
            {isSearchOpen ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    autoFocus
                    placeholder="Введите адрес..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {isSearching && (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                )}

                {!isSearching && searchResults.length > 0 && (
                  <Card>
                    <CardContent className="p-2">
                      {searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent transition-colors"
                          onClick={() => handleSelectSearchResult(result)}
                        >
                          {formatSearchResult(result)}
                        </button>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {!isSearching && searchQuery.length >= 3 && searchResults.length === 0 && (
                  <p className="text-sm text-muted-foreground py-2 text-center">
                    Ничего не найдено
                  </p>
                )}
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setIsSearchOpen(true)}
              >
                <Search className="w-4 h-4 mr-2" />
                Поиск адреса
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Zoom controls */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-1">
        <Button
          size="icon"
          variant="secondary"
          className="h-10 w-10 shadow-lg"
          onClick={handleZoomIn}
        >
          <Plus className="w-5 h-5" />
        </Button>
        <Button
          size="icon"
          variant="secondary"
          className="h-10 w-10 shadow-lg"
          onClick={handleZoomOut}
        >
          <Minus className="w-5 h-5" />
        </Button>
      </div>

      {/* Bottom panel */}
      <div className="absolute bottom-0 left-0 right-0 z-10 safe-area-inset-bottom">
        <div className="bg-white/95 backdrop-blur-sm shadow-lg m-3 rounded-xl p-4">
          {startPoint ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-blue-500" />
                <span className="text-muted-foreground">
                  {startPoint.lat.toFixed(5)}, {startPoint.lon.toFixed(5)}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={onCancel}
                >
                  Отмена
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleConfirm}
                >
                  Построить маршрут
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground text-center">
                Нажмите на карту, чтобы указать начальную точку
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={onCancel}
              >
                Отмена
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
